/**
 * AnalystPage Component
 * Landing page for eclou.analyst with hero section and mock analyses
 */

import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardHeader } from '@/components/layout/DashboardHeader';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { RequestCard } from '@/components/dashboard/RequestCard';
import { ProcessingStatus } from '@/types/api';
import type { ProcessingRequest } from '@/types/api';
import analystHero from '@/assets/analyst_hero.png';

const mockAnalyses: ProcessingRequest[] = [
  {
    id: 9001,
    productId: 1,
    productName: 'Multichannel-Kampagne',
    status: ProcessingStatus.Completed,
    createdAt: '2026-01-28T14:30:00Z',
    finishedAt: '2026-01-28T15:12:00Z',
    correlationId: 'multichannel-kampagne',
    attempts: 1,
    lastErrorMessage: null,
    updatedAt: '2026-01-28T15:12:00Z',
    userId: 'mock-user',
    userEmail: 'demo@eclou.net',
    userName: 'Demo User',
    payloadParametersCount: 3,
    featuresCount: 5,
    payloadParameters: [
      { parameterName: 'PLZ', payload: '80331' },
      { parameterName: 'Zielgruppe', payload: 'Privatkunden' },
      { parameterName: 'Branche', payload: 'E-Commerce' },
    ],
  },
  {
    id: 9002,
    productId: 1,
    productName: 'Lead-Offensive',
    status: ProcessingStatus.Completed,
    createdAt: '2026-02-03T09:15:00Z',
    finishedAt: '2026-02-03T10:01:00Z',
    correlationId: 'mock-lead-offensive',
    attempts: 1,
    lastErrorMessage: null,
    updatedAt: '2026-02-03T10:01:00Z',
    userId: 'mock-user',
    userEmail: 'demo@eclou.net',
    userName: 'Demo User',
    payloadParametersCount: 3,
    featuresCount: 5,
    payloadParameters: [
      { parameterName: 'PLZ', payload: '10115' },
      { parameterName: 'Zielgruppe', payload: 'Geschäftskunden' },
      { parameterName: 'Branche', payload: 'SaaS' },
    ],
  },
];

export default function AnalystPage() {
  const { t } = useTranslation();

  return (
    <DashboardLayout
      header={<DashboardHeader />}
      footer={<Footer />}
    >
      {/* Hero Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-5xl mx-auto py-8 lg:py-12">
        {/* Left: Text Content */}
        <div className="space-y-6">
          <h1 className="gradient-text text-3xl md:text-4xl lg:text-[2.75rem] font-bold leading-tight">
            {t('analyst.heroTitle')}
          </h1>
          <p className="text-base md:text-lg text-[var(--color-charcoal)]/70 leading-relaxed">
            {t('analyst.heroDescription')}
          </p>

          {/* CTA Button */}
          <Button
            className="btn-primary text-base md:text-lg px-8 py-3 rounded-full"
          >
            {t('analyst.ctaButton')}
          </Button>
        </div>

        {/* Right: Hero Illustration */}
        <div className="flex justify-center lg:justify-end">
          <img
            src={analystHero}
            alt={t('analyst.heroImageAlt')}
            className="w-full max-w-xl lg:max-w-none object-contain rounded-2xl"
          />
        </div>
      </section>

      {/* Recent analyses section */}
      <section className="max-w-5xl mx-auto mt-10">
        <h2 className="text-xl font-semibold text-[var(--color-charcoal)] mb-4">
          {t('requestCard.recentRequests')}
        </h2>
        <div
          className="grid gap-6 grid-cols-1 md:grid-cols-3"
          role="list"
          aria-label={t('requestCard.recentRequestsLabel')}
        >
          {mockAnalyses.map((analysis) => (
            <div key={analysis.id} role="listitem">
              <RequestCard request={analysis} />
            </div>
          ))}
          <div role="listitem">
            <RequestCard request={null} />
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}
